import { premiumCache } from './premium-cache.service.js'
import { apiService } from './api.service.js'

/**
 * Serviço Premium com Cache Inteligente
 * Evita consultas desnecessárias ao backend usando cache diário
 */
export class PremiumService {
  
  constructor() {
    this.currentUser = null
    this.premiumStatus = null
  }

  /**
   * Obtém status premium do usuário (com cache inteligente)
   * @param {boolean} forceRefresh - Força consulta ao backend
   * @returns {Promise<Object>} Status premium do usuário
   */
  async getPremiumStatus(forceRefresh = false) {
    try {
      // Obtém usuário atual
      const user = await this._getCurrentUser()
      if (!user) {
        throw new Error('Usuário não autenticado')
      }

      // Se não forçar refresh, tenta cache primeiro
      if (!forceRefresh) {
        const cached = premiumCache.getPremiumStatus(user.uid)
        if (cached) {
          this.premiumStatus = cached
          return cached
        }
      }

      // Consulta backend
      const response = await apiService.get('/users/profile')
      
      if (response.success) {
        const premiumData = {
          premium: response.data.premium || false,
          status: response.data.status || 'free',
          plan: response.data.plan,
          premiumSince: response.data.premiumSince,
          premiumExpires: response.data.premiumExpires,
          uid: response.data.uid,
          email: response.data.email,
          displayName: response.data.displayName,
          admin: response.data.admin || false
        }

        // Salva no cache
        premiumCache.setPremiumStatus(user.uid, premiumData)
        this.premiumStatus = premiumData
        
        return premiumData
      }

      throw new Error('Erro ao obter status premium')
      
    } catch (error) {
      console.error('Erro ao obter status premium:', error)
      
      // Se erro é de autenticação (timeout ou não autenticado),
      // NÃO cacheia resultado falso — permite retry na próxima chamada
      if (error.code === 'AUTH_TIMEOUT' || error.message === 'Usuário não autenticado') {
        return {
          premium: false,
          status: 'unknown',
          plan: null,
          premiumSince: null,
          premiumExpires: null
        }
      }

      // Em caso de erro genérico, retorna status free como fallback
      const fallbackStatus = {
        premium: false,
        status: 'free',
        plan: null,
        premiumSince: null,
        premiumExpires: null
      }
      
      this.premiumStatus = fallbackStatus
      return fallbackStatus
    }
  }

  /**
   * Verifica se usuário é premium (método rápido)
   * @returns {Promise<boolean>} Se usuário é premium
   */
  async isPremium() {
    const status = await this.getPremiumStatus()
    return status.premium === true && this._isPremiumValid(status)
  }

  /**
   * Verifica se premium ainda é válido
   * @param {Object} status - Status premium
   * @returns {boolean} Se premium é válido
   */
  _isPremiumValid(status) {
    if (!status.premium) return false
    
    // Premium vitalício
    if (!status.premiumExpires) return true
    
    // Verifica data de expiração
    const expirationDate = new Date(status.premiumExpires)
    return expirationDate > new Date()
  }

  /**
   * Obtém usuário atual do Firebase.
   * Aguarda a resolução do estado de autenticação via authService.waitForAuth()
   * para evitar retornar null antes do Firebase restaurar a sessão.
   * @returns {Promise<Object|null>} Usuário atual ou null
   */
  async _getCurrentUser() {
    const { authService } = await import('./auth.service.js')
    await authService.waitForAuth()
    this.currentUser = authService.getCurrentUser()
    return this.currentUser
  }

  /**
   * Força atualização do status premium
   * @returns {Promise<Object>} Novo status premium
   */
  async refreshPremiumStatus() {
    return this.getPremiumStatus(true)
  }

  /**
   * Limpa cache premium (útil no logout)
   */
  clearCache() {
    if (this.currentUser) {
      premiumCache.clearPremiumCache(this.currentUser.uid)
    }
    this.premiumStatus = null
    this.currentUser = null
  }

  /**
   * Obtém informações detalhadas do plano
   * @returns {Promise<Object>} Detalhes do plano
   */
  async getPlanDetails() {
    const status = await this.getPremiumStatus()
    
    const planInfo = {
      isPremium: status.premium,
      planType: status.plan,
      status: status.status,
      since: status.premiumSince,
      expires: status.premiumExpires,
      isValid: this._isPremiumValid(status),
      daysRemaining: null
    }

    // Calcula dias restantes
    if (status.premiumExpires) {
      const expirationDate = new Date(status.premiumExpires)
      const today = new Date()
      const diffTime = expirationDate - today
      planInfo.daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    return planInfo
  }

  /**
   * Verifica se funcionalidade específica está disponível
   * @param {string} feature - Nome da funcionalidade
   * @returns {Promise<boolean>} Se funcionalidade está disponível
   */
  async hasFeature(feature) {
    const isPremium = await this.isPremium()
    
    // Funcionalidades básicas sempre disponíveis
    const basicFeatures = ['contas', 'categorias', 'transacoes_basicas']
    if (basicFeatures.includes(feature)) return true
    
    // Funcionalidades premium
    const premiumFeatures = [
      'orcamentos',
      'relatorios_avancados', 
      'tags',
      'responsaveis_multiplos',
      'backup_automatico',
      'sincronizacao_multipla'
    ]
    
    if (premiumFeatures.includes(feature)) {
      return isPremium
    }
    
    // Funcionalidade desconhecida - assume premium
    return isPremium
  }

  /**
   * Obtém estatísticas do cache premium
   * @returns {Object} Estatísticas do cache
   */
  getCacheStats() {
    return premiumCache.getCacheStats()
  }
}

// Singleton instance
export const premiumService = new PremiumService()